import { useRef, useState } from "react";
import { useStorageObjects, useDeleteObject, useUpload, useUploadZip } from "../api/storage";
import { errorMessage } from "../api/client";
import ConfirmDialog from "../components/ConfirmDialog";
import { formatBytes, formatDate } from "../lib/format";

// Bundle upload (loose multipart OR a single self-describing .zip) + a storage object browser.
export default function UploadPage() {
  const [files, setFiles] = useState<File[]>([]);
  const [org, setOrg] = useState("org1");
  const [prefix, setPrefix] = useState("");
  const [dragging, setDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // single combined-zip uploader (POST /api/uploads/zip)
  const [zipFile, setZipFile] = useState<File | null>(null);
  const [zipDragging, setZipDragging] = useState(false);
  const zipInputRef = useRef<HTMLInputElement>(null);

  const upload = useUpload();
  const uploadZip = useUploadZip();
  const { data: objects, isLoading, error } = useStorageObjects(prefix || undefined);
  const del = useDeleteObject();

  function onUpload() {
    if (files.length === 0) return;
    const form = new FormData();
    form.set("org", org);
    files.forEach((f) => form.append("files", f));
    upload.mutate(form, {
      onSuccess: () => {
        setFiles([]);
        if (fileInputRef.current) fileInputRef.current.value = "";
      },
    });
  }

  function onUploadZip() {
    if (!zipFile) return;
    const form = new FormData();
    form.set("file", zipFile);
    uploadZip.mutate(form, {
      onSuccess: () => {
        setZipFile(null);
        if (zipInputRef.current) zipInputRef.current.value = "";
      },
    });
  }

  const [deletingKey, setDeletingKey] = useState<string | null>(null);
  function onDelete(key: string) {
    setDeletingKey(key);
  }

  function onDrop(e: React.DragEvent) {
    e.preventDefault();
    setDragging(false);
    const dropped = Array.from(e.dataTransfer.files);
    if (dropped.length) setFiles(dropped);
  }

  function onDropZip(e: React.DragEvent) {
    e.preventDefault();
    setZipDragging(false);
    const dropped = Array.from(e.dataTransfer.files);
    if (dropped.length) setZipFile(dropped[0]);
  }

  return (
    <div className="upload-page">
      <div className="page-head">
        <div>
          <h1 className="page-title">Manual Upload</h1>
          <p className="page-sub">Push a recording bundle into storage and enqueue processing.</p>
        </div>
      </div>

      <div className="upload-grid">
        <div className="upload-col">
        <section className="card upload-form">
          <div className="card-head">
            <h3>Upload a bundle</h3>
          </div>
          <p className="muted small">
            Select a bundle’s <code>metadata.json</code>, <code>*.data.zip</code> and{" "}
            <code>*.mp4</code>. The server registers a complete bundle and enqueues processing.
          </p>

          <label className="field">
            <span className="field-label">Org</span>
            <input
              className="text-input"
              value={org}
              onChange={(e) => setOrg(e.target.value)}
            />
          </label>

          <div
            className={dragging ? "dropzone dragging" : "dropzone"}
            onDragOver={(e) => {
              e.preventDefault();
              setDragging(true);
            }}
            onDragLeave={() => setDragging(false)}
            onDrop={onDrop}
            onClick={() => fileInputRef.current?.click()}
            role="button"
            tabIndex={0}
          >
            <input
              ref={fileInputRef}
              type="file"
              multiple
              hidden
              onChange={(e) => setFiles(Array.from(e.target.files ?? []))}
            />
            {files.length === 0 ? (
              <p className="muted">Drop files here, or click to choose</p>
            ) : (
              <ul className="file-chips">
                {files.map((f) => (
                  <li key={f.name} className="file-chip">
                    <span className="mono">{f.name}</span>
                    <span className="muted small">{formatBytes(f.size)}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="actions">
            <button
              className="btn primary"
              disabled={upload.isPending || files.length === 0}
              onClick={onUpload}
            >
              {upload.isPending ? "Uploading…" : `Upload${files.length ? ` (${files.length})` : ""}`}
            </button>
            {files.length > 0 && (
              <button className="btn ghost" onClick={() => setFiles([])} disabled={upload.isPending}>
                Clear
              </button>
            )}
          </div>

          {upload.isError && <p className="error">Upload failed: {errorMessage(upload.error)}</p>}
          {upload.data && (
            <p className="note">
              Wrote {upload.data.keys.length} object(s).
              {upload.data.recording_id ? ` Recording #${upload.data.recording_id}.` : ""}
              {upload.data.enqueued_job_id ? ` Enqueued job #${upload.data.enqueued_job_id}.` : ""}
            </p>
          )}
        </section>

        <section className="card upload-form">
          <div className="card-head">
            <h3>Upload a combined .zip</h3>
          </div>
          <p className="muted small">
            Drop a single self-describing bundle named{" "}
            <code>&lt;platform&gt;_&lt;org&gt;_&lt;uid&gt;_&lt;logId&gt;-&lt;user&gt;.zip</code>{" "}
            (<code>video.mp4</code> + CSVs + <code>metadata.json</code>). Works for both Mode 1 and
            Mode 2 recordings — org and uploader are read from the filename.
          </p>

          <div
            className={zipDragging ? "dropzone dragging" : "dropzone"}
            onDragOver={(e) => {
              e.preventDefault();
              setZipDragging(true);
            }}
            onDragLeave={() => setZipDragging(false)}
            onDrop={onDropZip}
            onClick={() => zipInputRef.current?.click()}
            role="button"
            tabIndex={0}
          >
            <input
              ref={zipInputRef}
              type="file"
              accept=".zip,application/zip"
              hidden
              onChange={(e) => setZipFile(e.target.files?.[0] ?? null)}
            />
            {!zipFile ? (
              <p className="muted">Drop a .zip here, or click to choose</p>
            ) : (
              <ul className="file-chips">
                <li className="file-chip">
                  <span className="mono">{zipFile.name}</span>
                  <span className="muted small">{formatBytes(zipFile.size)}</span>
                </li>
              </ul>
            )}
          </div>

          <div className="actions">
            <button
              className="btn primary"
              disabled={uploadZip.isPending || !zipFile}
              onClick={onUploadZip}
            >
              {uploadZip.isPending ? "Uploading…" : "Upload .zip"}
            </button>
            {zipFile && (
              <button
                className="btn ghost"
                onClick={() => setZipFile(null)}
                disabled={uploadZip.isPending}
              >
                Clear
              </button>
            )}
          </div>

          {uploadZip.isError && (
            <p className="error">Upload failed: {errorMessage(uploadZip.error)}</p>
          )}
          {uploadZip.data && (
            <p className="note">
              Wrote {uploadZip.data.keys.length} object(s).
              {uploadZip.data.recording_id ? ` Recording #${uploadZip.data.recording_id}.` : ""}
              {uploadZip.data.enqueued_job_id
                ? ` Enqueued job #${uploadZip.data.enqueued_job_id}.`
                : ""}
            </p>
          )}
        </section>
        </div>

        <section className="card storage-browser">
          <div className="card-head">
            <h3>Storage objects</h3>
            <input
              className="text-input compact"
              placeholder="filter by prefix"
              value={prefix}
              onChange={(e) => setPrefix(e.target.value)}
            />
          </div>

          {del.isError && <p className="error">Delete failed: {errorMessage(del.error)}</p>}
          {isLoading && <p className="muted small">Loading…</p>}
          {error && <p className="error">Failed to list objects: {errorMessage(error)}</p>}
          {objects && objects.objects.length === 0 && <p className="muted small">No objects.</p>}

          {objects && objects.objects.length > 0 && (
            <div className="table-wrap">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Key</th>
                    <th>Type</th>
                    <th>Size</th>
                    <th>Modified</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {objects.objects.map((o) => (
                    <tr key={o.key}>
                      <td className="mono">{o.key}</td>
                      <td className="muted small">{o.media_type ?? "—"}</td>
                      <td>{o.size != null ? formatBytes(o.size) : "—"}</td>
                      <td className="muted small">{formatDate(o.last_modified)}</td>
                      <td>
                        <button
                          className="btn reject small"
                          disabled={del.isPending}
                          onClick={() => onDelete(o.key)}
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </div>
      <ConfirmDialog
        open={deletingKey !== null}
        title="Delete storage object?"
        confirmLabel={del.isPending ? "Deleting…" : "Delete object"}
        tone="danger"
        busy={del.isPending}
        onConfirm={() =>
          deletingKey &&
          del.mutate(deletingKey, { onSettled: () => setDeletingKey(null) })
        }
        onCancel={() => setDeletingKey(null)}
      >
        <p className="mono" style={{ wordBreak: "break-all" }}>{deletingKey}</p>
      </ConfirmDialog>
    </div>
  );
}
